import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { DataType } from "shared/types";

import { MAIN_FIELDS_COLUMN_ID } from "pages/shared/Dashboard/constants";
import { getAttributeValue } from "pages/shared/schemaDefinitions/utils";

import Card from "features/components/Card";
import { ATTRIBUTE_CARD_TITLE_CLASSES } from "features/ui/Attachments/constants";
import AttributeRow from "features/ui/AttributeRow";
import { SchemaEntry } from "features/ui/MaterialTable";

interface Props {
  item: Record<string, any>;
  direction: "vertical" | "horizontal";
  attributes: string[][];
  title?: string;
  getSchemaForAccessor: (
    accessor: string,
    useShortLabel?: boolean
  ) => SchemaEntry<string> | undefined;
  mainFields?: string[];
  mainFieldsDirection?: "horizontal" | "vertical";
  useNewConfig?: boolean;
}

const NUMERIC_DATA_TYPES: DataType[] = [
  DataType.NUMBER,
  DataType.PERCENTAGE,
  DataType.RATIO_PERCENTAGE,
];

const AttributesCard = ({
  item,
  direction = "vertical",
  attributes,
  title,
  getSchemaForAccessor,
  mainFields,
  mainFieldsDirection = "vertical",
  useNewConfig = false,
}: Props) => {
  const { t } = useTranslation();
  const normalizedAttributes = (attributes ?? []).map((column) => column ?? []);
  const filteredAttributes = !useNewConfig
    ? normalizedAttributes.filter(
        (column) =>
          !(column.length === 1 && column[0] === MAIN_FIELDS_COLUMN_ID)
      )
    : normalizedAttributes;

  if (filteredAttributes.length === 0) {
    return null;
  }

  const isHorizontal = direction === "horizontal";
  const horizontalAttributeAccessors = filteredAttributes
    .flat()
    .filter((attr) => attr !== MAIN_FIELDS_COLUMN_ID);
  const allValuesNumeric =
    isHorizontal &&
    horizontalAttributeAccessors.length > 0 &&
    horizontalAttributeAccessors.every((attr) => {
      const dataType = getSchemaForAccessor(attr)?.dataType;

      return dataType !== undefined && NUMERIC_DATA_TYPES.includes(dataType);
    });

  return (
    <Card className="flex flex-col p-0! h-full!">
      {title ? (
        <div className={ATTRIBUTE_CARD_TITLE_CLASSES}>{title}</div>
      ) : null}
      <div className="flex flex-row flex-1 text-xs">
        {filteredAttributes.map((column, i) => {
          const isMainFieldsColumn =
            column.length === 1 && column[0] === MAIN_FIELDS_COLUMN_ID;

          if (isMainFieldsColumn && mainFields) {
            return (
              <div
                key={i}
                className={classNames(
                  "flex-1 border-r border-gray-100 flex flex-col gap-3 p-2.5",
                  {
                    "border-r-0": i === filteredAttributes.length - 1,
                  }
                )}
              >
                {mainFields.map((field) => {
                  const schema = getSchemaForAccessor(field);

                  const mainLabel = schema?.label ?? field;
                  const subLabel =
                    getSchemaForAccessor(field, true)?.label ?? field;

                  return (
                    <div
                      key={field}
                      className={classNames("flex gap-1.5", {
                        "flex-col": mainFieldsDirection === "vertical",
                        "justify-between flex-row":
                          mainFieldsDirection === "horizontal",
                      })}
                    >
                      <div className="font-bold">
                        {mainLabel}
                        {mainLabel !== subLabel && ` (${subLabel})`}
                      </div>
                      <div>
                        {getAttributeValue(
                          field,
                          item,
                          getSchemaForAccessor,
                          t
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          }

          return (
            <div
              key={i}
              className={classNames(
                "flex-1 border-r border-gray-100 flex flex-col gap-3 p-2.5",
                {
                  "border-r-0": i === filteredAttributes.length - 1,
                }
              )}
            >
              {column.map((attribute) => (
                <AttributeRow
                  key={attribute}
                  direction={direction}
                  valueAlign={allValuesNumeric ? "right" : "left"}
                  label={
                    getSchemaForAccessor(attribute, true)?.label ?? attribute
                  }
                  value={getAttributeValue(
                    attribute,
                    item,
                    getSchemaForAccessor,
                    t
                  )}
                />
              ))}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default AttributesCard;
